import { Autocomplete, Box, Chip } from '@mui/material'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { IPosition, IConnectedPosition, IPositionType } from '../../../../generated-types'
import { AppTextField } from 'src/components/AppTextField'
import { connectedPositionsRequired } from '../schema'

const getConnectedPositionLabel = (option: IConnectedPosition) => {
  const name = option?.positionConnected?.name ?? option?.name ?? ''
  const reference = option?.positionConnected?.reference?.trim()
  return reference ? `${name} (${reference})` : name
}

export const ConnectedPositionsFields: React.FC<{
  positions: IPosition[]
  positionId: EntityId
  name: string
  defaultValue?: IConnectedPosition[]
  connectingPositionType?: number
  maxConnectedPositions?: number
  disabled?: boolean
}> = ({
  positions,
  name,
  defaultValue = null,
  positionId,
  connectingPositionType,
  maxConnectedPositions = 2,
  disabled,
}) => {
  const { field } = useController({ name, defaultValue })
  const handleChange = val => field.onChange(val)
  const { t } = useTranslation(['position', 'common'])

  return (
    <Autocomplete
      value={field?.value ?? []}
      fullWidth
      multiple
      disabled={disabled}
      options={
        (positions as IPosition[])?.map(
          x =>
            ({
              positionConnectedId: x.id,
              positionId: positionId || 0,
              name: x.name,
              positionConnected: { id: x.id, name: x.name, reference: x.reference },
            } as IConnectedPosition),
        ) ?? []
      }
      renderOption={(props, option) => {
        return (
          <Box component="li" sx={{ display: 'flex', gap: '10px' }} {...props} key={option.positionConnectedId}>
            <Box>{getConnectedPositionLabel(option)}</Box>
          </Box>
        )
      }}
      isOptionEqualToValue={(option, value) => option.positionConnectedId === value.positionConnectedId}
      getOptionLabel={getConnectedPositionLabel}
      onChange={(_, v) => {
        const selectedPositions = (v as IConnectedPosition[]) ?? []
        if (selectedPositions.length > maxConnectedPositions) {
          toast.error(t('connectedPositionsMaxLimit', { ns: 'position', count: maxConnectedPositions }))
          return
        }
        const currentValues = (field?.value as IConnectedPosition[]) || []
        const updatedPositions = selectedPositions.map(selected => {
          const existing = currentValues.find(curr => curr.positionConnectedId === selected.positionConnectedId)
          return existing?.id ? { ...selected, id: existing.id } : selected
        })
        handleChange(updatedPositions)
      }}
      renderTags={(value: IConnectedPosition[], getTagProps) =>
        value?.map((option: IConnectedPosition, index: number) => {
          const { key, className: _, ...rest } = getTagProps({ index })
          return (
            <Chip
              key={key}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 0.1, mr: 0.1, mt: 0.5 }}
              label={getConnectedPositionLabel(option)}
              {...rest}
            />
          )
        })
      }
      renderInput={params => (
        <AppTextField
          {...params}
          label={t('connectedPositions')}
          name="connectedPositions"
          placeholder={t('connectedPositions')}
          required={connectedPositionsRequired(connectingPositionType as IPositionType)}
          InputProps={{ ...params.InputProps, endAdornment: null }}
        />
      )}
    />
  )
}
